import React, { useEffect, useState } from "react";
import { IoCamera } from "react-icons/io5";
import Camera from "react-html5-camera-photo";
import "react-html5-camera-photo/build/css/index.css";
import trans from "../../utils";
import Button from "../Helpers/Button/Button";
// Returns a src usable by <img>, whether the value is:
// - a raw base64 data URI string (legacy/local preview)
// - a { base64, filename } object produced by a fresh capture
// - an already-uploaded value (string filename or array of filenames) coming
//   back from a persisted submission, resolved against the public files host
function resolveImageSrc(rawValue) {
	if (!rawValue) return null;
	const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
	if (!value) return null;
	if (typeof value === "string") {
		if (value.startsWith("data:")) return value;
		if (value.startsWith("http://") || value.startsWith("https://")) return value;
		// Already-uploaded filename (e.g. "FS-....jpg"); resolve against the API host.
		const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/api.*$/, "");
		return `${apiBase}/files/formSubmissions/${value}`;
	}
	if (typeof value === "object") {
		return value.base64 || value.url || null;
	}
	return null;
}

export default function SnippetCamera({
	item,
	addData,
	setSubmitData,
	submitData,
	preview,
}) {
	const [camera, setCamera] = useState(false);
	const [value, setValue] = useState("");
	const currentValue = addData
		? (submitData?.find((i) => i.key === item.key)?.value ?? "")
		: (item?.value ?? value ?? "");
	const validate = addData
		? item?.required?.value && currentValue === ""
		: item?.required?.value && currentValue === "";
	const handleChange = (dataUri) => {
		if (addData) {
			setSubmitData((prev) => {
				// Remove existing item with same key if you want to replace it
				const filtered = prev?.filter((i) => i.key !== item.key) || [];
				return [
					...filtered,
					{
						key: item.key,
						name: item.name,
						title: item.title,
						// Wrap the captured photo so backend2's base64 upload
						// middleware (which looks for `value.base64`) can persist it.
						value: {
							base64: dataUri,
							filename: `${item.key}-${Date.now()}.jpg`,
						},
					},
				];
			});
			setCamera(!camera);
		}
	};
	const handleClear = () => {
		setCamera(false);
		setValue(null);
		if (addData) {
			setSubmitData((prev) => {
				const filtered = prev ? prev.filter((i) => i.key !== item.key) : [];
				return [
					...filtered,
					{
						key: item.key,
						name: item.name,
						title: item.title,
						value: null,
					},
				];
			});
		}
	};
	useEffect(() => {
		if (submitData?.length > 0) {
			// Match this specific field's key, not just any Camera field, so
			// forms with multiple Camera fields don't mix up previews.
			const file = submitData?.find((i) => i.key === item.key);
			setValue(file?.value ?? "");
		}
	}, [submitData, item.key]);
	const previewSrc = resolveImageSrc(addData ? value : (value || item?.value));
	return (
		<>
			<label
				style={{
					color: item?.css?.color,
					fontFamily: item?.css?.fontFamily,
					fontSize: item?.css?.fontSize,
					fontWeight: item?.css?.fontWeight,
				}}
				htmlFor={item?.key || "camera"}
				className="form-label d-block"
			>
				<div className="d-flex align-items-center">
					{item?.title}
					{item?.required?.value && !previewSrc && (
						<div className="text-danger ms-2">*</div>
					)}
				</div>
			</label>
			<IoCamera
				onClick={() => setCamera(!camera)}
				style={{ cursor: "pointer", height: "40px", width: "40px" }}
			/>
			{previewSrc && (
				<div className="d-flex align-items-end">
					<img
						src={previewSrc}
						alt="file"
						style={{
							width: "300px",
							height: "300px",
							borderRadius: "8px",
							display: "block",
						}}
					/>
					<Button type="default" onClick={handleClear} className="mx-2">
						{trans("Clear")}
					</Button>
				</div>
			)}
			{preview !== true ? (
				<>
					{validate && (
						<div className="text-danger mt-1">
							{trans(item?.required?.validationMessage)}
						</div>
					)}
				</>
			) : (
				""
			)}
			{camera && <Camera onTakePhoto={(dataUri) => handleChange(dataUri)} />}
		</>
	);
}
